'use no memo';

import {DataStatus} from 'platform/components';
import {Box} from 'platform/foundation';

import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {any, head, isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {Main, RestrictedArea, usePermissions} from '@dms/shared';
import {
  getAudit,
  useThunkDispatch,
  VehicleInspection,
  selectCarAudit,
  updateAuditData,
  getInitialAuditData,
} from '@dms/teas';

import {useFetchAuditStructure} from '../../hooks/useFetchAuditStructure';

export function InspectionDetailComponent() {
  const dispatch = useThunkDispatch();

  const {id: vehicleId, inspectionId} = useParams();

  const {fetchAuditStructure} = useFetchAuditStructure();
  const {loading} = useSelector(selectCarAudit);

  // Track which inspectionId is currently being loaded to prevent duplicate loads
  const loadingInspectionIdRef = useRef<string | null>(null);

  // Local loading state to track the full loading process (including manual steps)
  const [isLocalLoading, setIsLocalLoading] = useState(false);

  const isAuditLoading = any(isTrue, [
    isLocalLoading,
    loading.getAudit,
    loading.getAuditStructure,
    loading['vehicleCatalogue/getCustomTenantCatalogue'],
  ]);

  useEffect(() => {
    // Flag to track if this effect instance is stale (component unmounted or params changed)
    let isCancelled = false;

    if (isNotNil(vehicleId) && isNotNil(inspectionId)) {
      // Skip if already loading this exact inspection (prevent duplicate loads)
      if (loadingInspectionIdRef.current === inspectionId) {
        return;
      }

      loadingInspectionIdRef.current = inspectionId;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLocalLoading(true);

      dispatch(
        getAudit.action({
          auditId: inspectionId,
          withoutReducer: true,
        })
      )
        .unwrap()
        .then((response) => {
          if (isCancelled) {
            return;
          }

          if (isNotNil(response)) {
            const auditData = getInitialAuditData(head(response));
            dispatch(updateAuditData({auditData}));
            fetchAuditStructure(head(response));
          }
        })
        .finally(() => {
          if (!isCancelled) {
            setIsLocalLoading(false);
          }
        });
    }

    return () => {
      isCancelled = true;
      loadingInspectionIdRef.current = null;
      setIsLocalLoading(false);
    };
  }, [vehicleId, inspectionId]);

  return (
    <Main>
      <DataStatus isLoading={isAuditLoading} minHeight={100}>
        <VehicleInspection shouldUseAuditIdForUpdateTemplate={false} />
      </DataStatus>
    </Main>
  );
}

export function InspectionDetail() {
  const [hasReadInspectionPermission] = usePermissions({
    permissionKeys: ['readInspection'],
    scopes: {
      readInspection: {
        inspectionType: 'SKIP',
        participation: 'SKIP',
      },
    },
  });

  if (!hasReadInspectionPermission) {
    return (
      <Box flex={1} minWidth={0}>
        <RestrictedArea />
      </Box>
    );
  }

  return <InspectionDetailComponent />;
}
